<template>
  <div>
    <v-data-table :headers="headers" :items="drivers" :options.sync="options" :loading="tableLoading"
                  :server-items-length="totalDrivers" item-key="username" :mobile-breakpoint="0"
                  multi-sort
                  style="height:auto;" :footer-props="footerProps" class="elevation-1">

      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ $vuetify.lang.t(`$vuetify.carParks.header`) }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <tooltip-basic :text="$vuetify.lang.t(`$vuetify.carParks.driverIcons.create`)" :icon="addIcon" :emit-state="`open-entry-dialog`"
                         @open-entry-dialog="prepareDriverEntry(); entryDialog = true"></tooltip-basic>
        </v-toolbar>
      </template>

      <template v-for="item in iconedHeaders" v-slot:[`header.${item}`]="{header}">
              <span :key="item" class="d-inline-flex align-center justify-start"
                    style="vertical-align: middle; word-wrap: break-word">
                <v-icon left>{{ header.icon }}</v-icon>
                <span>{{ header.text }}</span>
              </span>
      </template>

      <template v-slot:item.tripsAfter="{item}">
        {{ item.tripsAfterFormatted }}
      </template>

      <template v-slot:item.tripsBefore="{item}">
        {{ item.tripsBeforeFormatted }}
      </template>

      <template v-slot:item.role="{item}">
        {{ $vuetify.lang.t(`$vuetify.carParks.roles.${item.role}`) }}
      </template>

      <template v-slot:item.restricted="{item}">
        <v-simple-checkbox v-model="item.restricted" :disabled="true"></v-simple-checkbox>
      </template>

      <template v-slot:item.actions="{item}">
        <tooltip-basic :text="$vuetify.lang.t(`$vuetify.carParks.driverIcons.edit`)"
                       :icon="editIcon"
                       :emit-state="`edit-driver-entry`"
                       @edit-driver-entry="editDriverEntry(item)">
        </tooltip-basic>
        <tooltip-basic :text="$vuetify.lang.t(`$vuetify.carParks.driverIcons.remove`)"
                       :icon="removeIcon"
                       :emit-state="`remove-driver-entry`"
                       @remove-driver-entry="openRemoveDialog(item)">
        </tooltip-basic>
      </template>

    </v-data-table>

    <car-par-driver-entry
      :dialog="entryDialog"
      :editEntry.sync="driverEntry"
      :title="driverEntryTitle"
      @close-driver-dialog="entryDialog = false"
      @send-edited-entry="sendDriverEntry">
    </car-par-driver-entry>

    <confirmation-dialog :dialog="removeEntryDialog"
                         :title="$vuetify.lang.t(`$vuetify.carParks.removeDriverDialog.title`)"
                         :text="$vuetify.lang.t(`$vuetify.carParks.removeDriverDialog.text`, driverEntry.driver)"
                         :reject="$vuetify.lang.t('$vuetify.map.criticalPopup.allCameraCorrectDialog.reject')"
                         :confirm="$vuetify.lang.t('$vuetify.map.criticalPopup.allCameraCorrectDialog.confirm')"
                         @confirmation-dialog-closed="removeEntryDialog = false"
                         @confirmation-dialog-confirmed="removeDriveEntry">
    </confirmation-dialog>

  </div>
</template>

<script>

import { defineTableHeader } from '@/utils/tableManager'
import {
  mdiAccount, mdiAccountEdit, mdiAccountOff,
  mdiAccountPlus,
  mdiAccountRemove,
  mdiBookOpenVariant,
  mdiTimerSandEmpty,
  mdiTimerSandFull
} from '@mdi/js'
import backend from '@/backend'
import { CarParkDriver } from '@/models/carParks/carParkDriver'
import TooltipBasic from '@/components/utils/tooltipBasic'
import CarParDriverEntry from '@/components/map/carPark/carParDriverEntry'
import { defaultCarParkDriverEntry, userToParkDriverEntry } from '@/utils/carParks'
import { CarParkDriverEditModel } from '@/models/carParks/carParkDriverEditModel'
import ConfirmationDialog from '@/components/utils/confirmationDialog'
import { carParkRole, ComponentMode } from '@/enum'
import snackbarMixin from '@/mixins/snackbarMixin'
import { CarParkDriverRemoveModel } from '@/models/carParks/carParkDriverRemoveModel'

const defaultFilterState = () => ({
  username: {
    type: 'text',
    value: '',
    specialOnly: true,
    header: '$vuetify.map.managerMap.filterHeaders.driverHeader'
  }
})

export default {
  name: 'carParkDrivers',
  components: { ConfirmationDialog, CarParDriverEntry, TooltipBasic },
  mixins: [snackbarMixin],
  props: {
    carParkId: Number
  },
  data: () => ({
    drivers: [],
    totalDrivers: 0,
    options: {
      sortBy: ['username'],
      sortDesc: [false],
      itemsPerPage: 10,
      page: 1
    },
    filters: defaultFilterState(),
    footerProps: { 'items-per-page-options': [5, 10, 25, 50, -1] }, // if add more - page overflow can be reached!
    tableLoading: false,

    entryDialog: false,
    removeEntryDialog: false,
    driverEntry: defaultCarParkDriverEntry(),
    driverEntryTitle: '$vuetify.carParks.driverForm.titles.create',
    addIcon: mdiAccountPlus,
    editIcon: mdiAccountEdit,
    removeIcon: mdiAccountRemove
  }),
  computed: {
    headers () {
      return [
        defineTableHeader('$vuetify.map.managerMap.headers.username', 'username', mdiAccount),
        defineTableHeader('$vuetify.carParks.role', 'role', mdiBookOpenVariant),
        defineTableHeader('$vuetify.carParks.after', 'tripsAfter', mdiTimerSandFull),
        defineTableHeader('$vuetify.carParks.before', 'tripsBefore', mdiTimerSandEmpty),
        defineTableHeader('$vuetify.carParks.restricted', 'restricted', mdiAccountOff),
        defineTableHeader('$vuetify.carParks.actions', 'actions', null, false, '')
      ]
    },
    iconedHeaders () {
      return this.headers.filter(header => header.hasOwnProperty('icon')).map(header => header.value)
    }
  },
  methods: {
    async getUsers (data = { includedDriver: null }) {
      this.tableLoading = true
      try {
        const result = await backend.searchCarParkDrivers({
          ...this.options,
          filters: this.filters,
          carParkId: this.carParkId,
          includedDriver: data.includedDriver
        })

        if ('data' in result && result.data) {
          this.drivers = result.data.items.map(item => new CarParkDriver({ ...item }))
          this.totalDrivers = result.data.total
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.tableLoading = false
      }
    },
    editDriverEntry (entry) {
      this.driverEntryTitle = `$vuetify.carParks.driverForm.titles.${ComponentMode.EDIT}`
      this.driverEntry = userToParkDriverEntry(entry)
      this.entryDialog = true
    },
    openRemoveDialog (entry) {
      this.driverEntry = userToParkDriverEntry(entry)
      this.removeEntryDialog = true
    },
    prepareDriverEntry () {
      this.driverEntryTitle = `$vuetify.carParks.driverForm.titles.${ComponentMode.CREATE}`
      this.driverEntry = defaultCarParkDriverEntry()
    },
    async sendDriverEntry (entry) {
      this.entryDialog = false

      const data = new CarParkDriverEditModel({
        username: entry.driver,
        carParkId: this.carParkId,
        role: entry.role,
        after: entry.isAfterUsed && entry.role === carParkRole.MANAGER ? entry.after.getTime() : null,
        before: entry.isBeforeUsed && entry.role === carParkRole.MANAGER ? entry.before.getTime() : null,
        restricted: entry.role === carParkRole.MANAGER ? entry.restricted : false
      })

      try {
        this.tableLoading = true
        await backend.editCarParkDriver(data.backendModel())
        await this.getUsers({ includedDriver: entry.driver })
        this.openSuccessSnackbar(`$vuetify.carParks.driverForm.success.${ComponentMode.CREATE}`)
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.tableLoading = false
      }
    },
    async removeDriveEntry () {
      const data = new CarParkDriverRemoveModel({
        username: this.driverEntry.driver,
        carParkId: this.carParkId
      })

      try {
        this.tableLoading = true
        await backend.removeCarParkDriver(data.backendModel())
        await this.getUsers()
        this.openSuccessSnackbar(`$vuetify.carParks.driverForm.success.${ComponentMode.REMOVE}`)
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.tableLoading = false
      }
    }
  },
  watch: {
    options: {
      async handler () {
        // we don't use mount for initial loading because first data loading occures here
        await this.getUsers()
      },
      deep: true
    }
  }
}
</script>

<style scoped>

</style>
